import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import type { SecurityAgentApiRow } from 'src/types/mindtime/wazuhAgents';

type SecurityAgentContextValue = {
  selectedAgent: SecurityAgentApiRow | null;
  setSelectedAgent: React.Dispatch<React.SetStateAction<SecurityAgentApiRow | null>>;
  agents: SecurityAgentApiRow[];
  agentsLoading: boolean;
  agentsError: string | null;
  refreshAgents: () => void;
  clearSelectedAgent: () => void;
};

const SecurityAgentContext = createContext<SecurityAgentContextValue>({
  selectedAgent: null,
  setSelectedAgent: () => {},
  agents: [],
  agentsLoading: false,
  agentsError: null,
  refreshAgents: () => {},
  clearSelectedAgent: () => {},
});

export const SecurityAgentProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [selectedAgent, setSelectedAgent] = useState<SecurityAgentApiRow | null>(null);
  const [agents, setAgents] = useState<SecurityAgentApiRow[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshAgents = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAgentsLoading(true);
    setAgentsError(null);

    wazuhAPI
      .getAgents()
      .then((rows) => {
        if (!cancelled) {
          setAgents(rows);
          setAgentsError(null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setAgents([]);
          setAgentsError(error instanceof Error ? error.message : 'Failed to load agents.');
        }
      })
      .finally(() => {
        if (!cancelled) setAgentsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const clearSelectedAgent = useCallback(() => {
    setSelectedAgent(null);
  }, []);

  return (
    <SecurityAgentContext.Provider
      value={{
        selectedAgent,
        setSelectedAgent,
        agents,
        agentsLoading,
        agentsError,
        refreshAgents,
        clearSelectedAgent,
      }}
    >
      {children}
    </SecurityAgentContext.Provider>
  );
};

export const useSecurityAgentContext = (): SecurityAgentContextValue => useContext(SecurityAgentContext);
